import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../ui/button';
import { Trophy, Timer, Zap } from 'lucide-react';

const PerfectTimingTap = ({ onGameEnd }) => {
  const canvasRef = useRef(null);
  const [gameState, setGameState] = useState('waiting');
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(60);
  const [targetProgress, setTargetProgress] = useState(0);
  const [targetSpeed, setTargetSpeed] = useState(1.5);
  const [streak, setStreak] = useState(0);
  const [feedback, setFeedback] = useState('');
  const gameLoopRef = useRef(null);
  const directionRef = useRef(1);

  useEffect(() => {
    if (gameState === 'playing') {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext('2d');
      const width = canvas.width;
      const height = canvas.height;

      const gameLoop = () => {
        // Clear canvas
        const gradient = ctx.createLinearGradient(0, 0, 0, height);
        gradient.addColorStop(0, '#1a1a2e');
        gradient.addColorStop(1, '#0f0f1e');
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, width, height);

        // Draw perfect zone
        const zoneY = height / 2;
        const zoneHeight = 60;
        
        const zoneGradient = ctx.createLinearGradient(0, zoneY - zoneHeight / 2, 0, zoneY + zoneHeight / 2);
        zoneGradient.addColorStop(0, 'rgba(34, 197, 94, 0.1)');
        zoneGradient.addColorStop(0.5, 'rgba(34, 197, 94, 0.3)');
        zoneGradient.addColorStop(1, 'rgba(34, 197, 94, 0.1)');
        
        ctx.fillStyle = zoneGradient;
        ctx.fillRect(0, zoneY - zoneHeight / 2, width, zoneHeight);
        
        ctx.strokeStyle = '#22c55e';
        ctx.lineWidth = 3;
        ctx.strokeRect(0, zoneY - zoneHeight / 2, width, zoneHeight);

        // Draw center line
        ctx.strokeStyle = '#fbbf24';
        ctx.lineWidth = 2;
        ctx.setLineDash([10, 5]);
        ctx.beginPath();
        ctx.moveTo(0, zoneY);
        ctx.lineTo(width, zoneY);
        ctx.stroke();
        ctx.setLineDash([]);

        // Update progress
        setTargetProgress(prev => {
          let newProgress = prev + (targetSpeed * directionRef.current);
          
          if (newProgress >= 100) {
            newProgress = 100;
            directionRef.current = -1;
          } else if (newProgress <= 0) {
            newProgress = 0;
            directionRef.current = 1;
          }
          
          return newProgress;
        });

        // Draw moving bar
        const barY = 50 + (targetProgress / 100) * (height - 100);
        const barHeight = 15;
        
        const barGradient = ctx.createLinearGradient(0, barY - barHeight / 2, 0, barY + barHeight / 2);
        barGradient.addColorStop(0, '#f59e0b');
        barGradient.addColorStop(0.5, '#fbbf24');
        barGradient.addColorStop(1, '#f59e0b');
        
        ctx.fillStyle = barGradient;
        ctx.fillRect(0, barY - barHeight / 2, width, barHeight);
        
        // Bar glow effect
        ctx.shadowColor = '#fbbf24';
        ctx.shadowBlur = 20;
        ctx.fillRect(0, barY - barHeight / 2, width, barHeight);
        ctx.shadowBlur = 0;

        // Draw progress indicator
        ctx.fillStyle = 'rgba(255, 255, 255, 0.1)';
        ctx.fillRect(width - 30, 0, 30, height);
        
        ctx.fillStyle = '#3b82f6';
        const indicatorY = 50 + (targetProgress / 100) * (height - 100);
        ctx.fillRect(width - 30, indicatorY - 5, 30, 10);

        gameLoopRef.current = requestAnimationFrame(gameLoop);
      };

      gameLoop();

      return () => {
        if (gameLoopRef.current) {
          cancelAnimationFrame(gameLoopRef.current);
        }
      };
    }
  }, [gameState, targetProgress, targetSpeed]);

  useEffect(() => {
    if (gameState === 'playing' && timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            setGameState('finished');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [gameState, timeLeft]);

  useEffect(() => {
    if (gameState === 'finished') {
      // Stop the game loop
      if (gameLoopRef.current) {
        cancelAnimationFrame(gameLoopRef.current);
      }
      // Call onGameEnd after a short delay to show the final screen
      setTimeout(() => {
        onGameEnd(score);
      }, 2000);
    }
  }, [gameState, score, onGameEnd]);

  const handleTap = () => {
    if (gameState !== 'playing') return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const height = canvas.height;
    const barY = 50 + (targetProgress / 100) * (height - 100);
    const zoneY = height / 2;
    const distance = Math.abs(barY - zoneY);

    let points = 0;
    let result = '';

    if (distance < 10) {
      points = 100;
      result = '🎯 PERFECT!';
      setStreak(prev => prev + 1);
      setTargetSpeed(prev => Math.min(prev + 0.1, 3));
    } else if (distance < 20) {
      points = 50;
      result = '✨ Great!';
      setStreak(prev => prev + 1);
    } else if (distance < 30) {
      points = 25;
      result = '👍 Good';
      setStreak(0);
    } else {
      points = 0;
      result = '❌ Missed';
      setStreak(0);
      setTargetSpeed(1.5);
    }

    // Streak bonus
    const bonusPoints = Math.floor(streak / 3) * 20;
    points += bonusPoints;

    setScore(prev => prev + points);
    setFeedback(result);
    setTimeout(() => setFeedback(''), 800);
  };

  const startGame = () => {
    setGameState('playing');
    setScore(0);
    setTimeLeft(60);
    setTargetProgress(0);
    setTargetSpeed(1.5);
    setStreak(0);
    directionRef.current = 1;
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="bg-gradient-to-br from-blue-900/30 to-purple-900/30 rounded-lg p-4 border border-blue-500/30">
        {/* Game Stats */}
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-2 text-yellow-400">
            <Trophy className="w-5 h-5" />
            <span className="font-bold text-xl">Score: {score}</span>
          </div>
          <div className="flex items-center gap-2 text-blue-400">
            <Timer className="w-5 h-5" />
            <span className="font-bold text-xl">{timeLeft}s</span>
          </div>
          {streak >= 3 && gameState === 'playing' && (
            <div className="flex items-center gap-2 text-orange-400 animate-pulse">
              <Zap className="w-5 h-5" />
              <span className="font-bold text-lg">x{streak} Streak!</span>
            </div>
          )}
        </div>

        {/* Feedback */}
        {feedback && (
          <div className="text-center mb-2">
            <span className="text-2xl font-bold text-yellow-400 animate-bounce">
              {feedback}
            </span>
          </div>
        )}

        {/* Game Canvas */}
        <div className="relative bg-black rounded-lg overflow-hidden mb-4">
          <canvas
            ref={canvasRef}
            width={320}
            height={480}
            className="w-full h-auto max-h-[480px]"
          />
          
          {gameState === 'waiting' && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/80">
              <Zap className="w-16 h-16 text-yellow-400 mb-4" />
              <h3 className="text-2xl font-bold text-white mb-2">Perfect Timing Tap</h3>
              <p className="text-gray-300 text-center mb-4 max-w-md px-4">
                Tap when the moving bar reaches the green zone!
                Perfect timing = maximum points. Build streaks for speed bonus!
              </p>
              <Button onClick={startGame} className="gaming-button">
                Start Game
              </Button>
            </div>
          )}

          {gameState === 'finished' && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/90">
              <Trophy className="w-20 h-20 text-yellow-400 mb-4" />
              <h3 className="text-3xl font-bold text-white mb-2">Game Over!</h3>
              <p className="text-2xl text-yellow-400 font-bold mb-4">Final Score: {score}</p>
              <p className="text-gray-300 mb-6">Submitting your score...</p>
            </div>
          )}
        </div>

        {/* Tap Button */}
        <Button
          onClick={handleTap}
          disabled={gameState !== 'playing'}
          className="gaming-button w-full h-20 text-2xl font-bold"
        >
          TAP NOW!
        </Button>

        {/* Instructions */}
        <div className="mt-4 text-sm text-gray-400 text-center">
          <p>⚡ Tap when the bar is in the green zone</p>
          <p>🎯 Perfect hits = 100 points • Great = 50 points</p>
          <p>🔥 Build streaks to increase speed and challenge!</p>
        </div>
      </div>
    </div>
  );
};

export default PerfectTimingTap;
